import { useEffect, useState } from "react";
import { useAuth } from "../../lib/auth";
import { api, apiJSON } from "../../lib/api";
import { useIntl } from "../../lib/i18n";

type Site = {
  themeConfig?: Record<string, unknown>;
  sectionOrder?: string[];
  jurisdictions?: string[];
  practiceCharter?: string;
};

export default function SitePage() {
  const { session } = useAuth();
  const { t } = useIntl();
  const [jurisdictionsText, setJurisdictionsText] = useState("");
  const [charter, setCharter] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!session) return;
    apiJSON<Site>("/api/v1/tenant/site", { token: session.token })
      .then((s) => {
        setJurisdictionsText((s.jurisdictions ?? []).join("\n"));
        setCharter(s.practiceCharter ?? "");
        setLoading(false);
      })
      .catch((e) => {
        setError(String(e.message ?? e));
        setLoading(false);
      });
  }, [session]);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const jurisdictions = jurisdictionsText
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await api("/api/v1/tenant/site", {
        method: "PUT",
        token: session?.token,
        body: JSON.stringify({
          jurisdictions,
          practiceCharter: charter,
          themeConfig: {},
          sectionOrder: [],
        }),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => res.statusText));
      setSaved(true);
    } catch (err) {
      setError(String((err as Error).message ?? err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold text-brand-900">{t("site.title")}</h1>
      <p className="mt-2 text-sm text-slate-500">
        Drafting context seeding — what you put here goes into <em>every</em> LLM-generated draft as
        the system prompt header. Without it, Claude defaults to US-federal procedure (FRCP, USC).
      </p>

      {loading ? (
        <p className="mt-6 text-slate-500">{t("common.loading")}</p>
      ) : (
        <form onSubmit={save} className="mt-6 max-w-2xl space-y-4">
          <div>
            <label className="block text-sm font-medium text-brand-900">Jurisdictions</label>
            <p className="text-xs text-slate-500">
              One per line. e.g. "Utah state courts", "US federal — 10th Circuit", "USCIS national".
            </p>
            <textarea
              rows={5}
              value={jurisdictionsText}
              onChange={(e) => setJurisdictionsText(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-sm"
              placeholder={"Utah state courts\nUS federal — 10th Circuit"}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-900">Practice charter</label>
            <p className="text-xs text-slate-500">
              One paragraph. What does this firm do, for whom, against which forum? Claude
              uses it to ground every draft.
            </p>
            <textarea
              rows={5}
              value={charter}
              onChange={(e) => setCharter(e.target.value)}
              className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder="Wasatch Immigration Law represents non-citizens in asylum, family-based petitions, and deportation defense. All filings target USCIS, EOIR, or the BIA."
            />
          </div>

          {error && <p className="text-sm text-rose-600">{error}</p>}
          {saved && <p className="text-sm text-emerald-700">Saved.</p>}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-500 disabled:opacity-50"
            >
              {saving ? "…" : "Save"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
